/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for anypurpose without the express written permission of Honeywell. */
import React, { useEffect, useRef, useState } from "react";
import { Grid, Button, Paper, Typography } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ConfigurationVariables } from "../../config";
import TextInputField from "../../utils/text-input-field/TextInputField";
import AcceptTerms from "../../utils/accept-terms/AcceptTerms";
import InfoModal from "../../utils/modals/info-modal/InfoModal";
import Loader from "../Loader/Loader";
import UploadProfilePic from "../../utils/upload-profile-pic/UploadProfilePic";
import AutoCompleteMultiSelect from "../../utils/multiselect-dropdown/AutoCompleteMultiSelect";

import "../UserManagement/CreateUser";
import useFetch from "../../hooks/useFetch";
import { IErrorProps } from "../../models";
import ToastBarForError from "../SharedComponents/ToastBarForError";
import { getAccessToken } from "../../utils";
import { AppDispatch } from "../../store";
import { useDispatch } from "react-redux";
import { userActions } from "../../store/User/user-slice";
import { getRolesWithTypeProp } from "../../utils/methods/roles";
import editLogo from "../../images/DefaultProfilePic.png";

/**
 *
 * @summery for editing honeywell user info like profile pic, roles
 */
function EditHoneywellUser() {
  const { UserMgmt_Endpoints, Api, Version, UserController } =
    ConfigurationVariables;
  let usersUrl = `${UserMgmt_Endpoints}/${Api}/${Version}/${UserController}`;
  const rolesUrl = `${usersUrl}/roles`;
  const config = {
    headers: {
      accept: "application/json",
      customerId: "honeywell", //@TODO: remove hardcod client id
      Authorization: `Bearer ${getAccessToken()}`,
    },
  };
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isLoading: isFetchRolesLoading,
    data: rolesData,
    error: fetchRolesError,
  } = useFetch(rolesUrl, config);

  const userDetailsToEdit = location?.state?.userDetailsToEdit || null;
  const { UserInfo, CustomerId } = userDetailsToEdit;
  const [userId, setUserId] = useState<string>(UserInfo?.userId);
  const [existingProfilePic, setExistingProfilePic] =
    useState<string>(editLogo);
  const [roleOptions, setRoleOptions] = useState<any>([]);
  const [firstName, setFirstName] = useState(UserInfo?.firstName);
  const [lastName, setLastName] = useState(UserInfo?.lastName);
  const [emailId] = useState(UserInfo?.emailId);
  const [roleId, setRoleIds] = useState<string[]>([]);
  const [defaultRoles, setDefaultRoles] = useState<string[]>([]);
  const [readOnlyDefaultRoleOptions, setReadOnlyDefaultRoles] = useState<any[]>(
    []
  );
  const [userProfilePic, setUserProfilePic] = useState<any>();
  const [isRoleIdEmpty, setIsRoleIdEmpty] = useState<boolean>(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false);
  const [isFormFilled, setIsFormFilled] = useState<boolean>(false);

  const [successModal, setSuccessModal] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [currentCustomerID, setCurrentCustomerID] = useState<string | null>(
    CustomerId
  );
  const [errorList, setErrorList] = useState<IErrorProps[]>([]);
  const [openToastError, setOpenToastError] = useState(false);
  const [isFormDataChanged, setIsFormDataChanged] = useState<boolean>(false);
  let isUserUpdated = useRef({
    profilePicUpdate: false,
    rolesUpdate: false,
  });

  const dispatch: AppDispatch = useDispatch();

  /**To get the user profile pic and show the preview */
  useEffect(() => {
    let uploadProfilePicUrl = `${usersUrl}/userpic/${userId}`;
    setLoading(true);
    setExistingProfilePic(editLogo);
    axios
      .get(uploadProfilePicUrl, config)
      .then((response) => {
        setExistingProfilePic(response.data);
        setLoading(false);
        if (response.status !== 204) {
          setExistingProfilePic(response.data);
        } else {
          setExistingProfilePic(editLogo);
        }
      })
      .catch((error) => {
        setLoading(false);
        setErrorModal(true);
        setErrorMsg(error?.message);
      });
  }, []);

  /**
   * To get roles options for the assign roles dropdown*/
  useEffect(() => {
    const rolesDataMapped =
      rolesData?.length > 0 &&
      rolesData.map((role: any) => {
        return {
          label: role.name,
          value: role.roleId,
          ...role,
        };
      });

    if (fetchRolesError) {
      let errorObj = {
        errorCode: fetchRolesError?.response?.status,
        errorMessage: fetchRolesError?.message,
      };

      setErrorList((prevList) => [...prevList, errorObj]);
      setOpenToastError(true);
    }

    setRoleOptions(rolesDataMapped ? rolesDataMapped : []);

    if (rolesData) {
      /**To set the default roles
       * Checking if the default role is present in the role options (get roles api resp)
       * and filter if it is not present in the response
       */
      let roleIds = rolesData?.map((role: any) => role.roleId);

      let defaultOpsFiltered = [];
      defaultOpsFiltered = UserInfo.roles.filter((defaultRole: any) =>
        roleIds.includes(defaultRole.roleId)
      );
      defaultOpsFiltered = defaultOpsFiltered.map((role: any) => {
        return {
          label: role.name,
          value: role.roleId,
          ...role,
        };
      });

      setRoleIds(defaultOpsFiltered.map((obj: any) => obj.roleId));
      setDefaultRoles(defaultOpsFiltered);

      /**To filter readOnly Default Roles(which are not in get roles api response)
       * and these can not be modifed/added from UI
       */
      let readOnlyDefaultRoles: any[] = [];
      readOnlyDefaultRoles = UserInfo.roles.filter(
        (defaultRole: any) => !roleIds.includes(defaultRole.roleId)
      );
      readOnlyDefaultRoles = readOnlyDefaultRoles.map((role: any) => {
        return {
          label: role.name,
          value: role.roleId,
          ...role,
        };
      });

      setReadOnlyDefaultRoles(readOnlyDefaultRoles);
    }
  }, [fetchRolesError, rolesData]);

  /**
   *
   * @param event
   * @param values selected values - list of objects
   * @param reason
   *
   * sets the selected options in the state
   */
  const assignRolesHandler = (event: React.ChangeEvent<{}>, values: any[]) => {
    const selectedRoleValues = values && values.map((obj) => obj.value);

    setRoleIds(selectedRoleValues);
    selectedRoleValues?.length > 0 && setIsRoleIdEmpty(false);

    /**Checking whether form data changed or not */
    let defaultRoleIds = defaultRoles.map((obj: any) => obj.roleId);
    if (
      defaultRoleIds.length === selectedRoleValues.length &&
      defaultRoleIds.every((el) => selectedRoleValues.includes(el))
    ) {
      /**Form data not changed */
      isUserUpdated.current = { ...isUserUpdated.current, rolesUpdate: false };
    } else {
      isUserUpdated.current = { ...isUserUpdated.current, rolesUpdate: true };
    }
    handleEnableOrDisableUpdateButton();
  };

  // Enable or disable user update button
  const handleEnableOrDisableUpdateButton = () => {
    const isAnyValueTrue = Object.values(isUserUpdated.current).some(
      (value) => value
    );

    if (isAnyValueTrue) {
      setIsFormDataChanged(true);
    } else {
      setIsFormDataChanged(false);
    }
  };

  const cancelBtnHandler = () => {
    navigate(-1);
  };

  /**
   * Checking is termas accepted and
   * Uploading profile pic (only if user pic changed)
   */
  const uploadProfilePicture = () => {
    if (userProfilePic) {
      if (!isTermsAccepted) {
        setIsFormFilled(true);
        setIsTermsAccepted(false);
      } else {
        submitUploadedProfilePic();
      }
    }
  };

  /**
   * Checks if the roleId and terms selected
   * and call the updateUserRoles function
   */
  const callUpdateUserRoles = () => {
    if (roleId && !isTermsAccepted) {
      setIsFormFilled(true);
      setIsTermsAccepted(false);
    } else {
      setErrorModal(false);
      updateUserRoles();
    }
  };

  /**
   * @summery
   * input fields validation: empty field check
   * email basic validation
   * opens confirm modal if all mandatory fields entered
   *
   * no api call incase of no change in form data
   */
  const updateBtnHandler = () => {
    if (readOnlyDefaultRoleOptions?.length === 0 && roleId?.length == 0) {
      setIsRoleIdEmpty(true);
    } else {
      let defaultRoleIds = defaultRoles.map((obj: any) => obj.roleId);
      if (
        defaultRoleIds.length === roleId.length &&
        defaultRoleIds.every((el) => roleId.includes(el))
      ) {
      } else {
        callUpdateUserRoles();
      }
    }

    uploadProfilePicture();
  };

  /**
   *
   * Updating selected user roles
   */
  const updateUserRoles = () => {
    let reqBody = {
      roles: getRolesWithTypeProp(defaultRoles, roleId),
    };

    setLoading(true);
    axios
      .patch(`${usersUrl}/${userId}`, reqBody, config)
      .then(() => {
        setLoading(false);
        setSuccessModal(true);

        // need to dispatch after editing a Honeywell user
        // No info of updated User comes from API , so using the roleIds of request API
        // to update the new roles for the user
        // Pass the userId and roleIds array to reducer.
        dispatch(
          userActions.updateUserRoles({
            userId: userId,
            roleIds: roleId,
          })
        );
      })
      .catch((error) => {
        setLoading(false);
        setErrorModal(true);
        setErrorMsg(error?.message);

        let errorObj = {
          errorCode: error?.response?.status,
          errorMessage: error?.message,
        };
        setErrorList((prevList) => [...prevList, errorObj]);
        setOpenToastError(true);
      })
      .finally(() => {});
  };

  /**
   *
   * For submitting uploaded profile pic to save in database
   */
  const submitUploadedProfilePic = () => {
    let uploadProfilePicUrl = `${usersUrl}/userpic/${userId}`;
    let formData = new FormData();
    formData.append("logo", userProfilePic);
    if (userProfilePic) {
      setLoading(true);
      axios
        .put(uploadProfilePicUrl, formData, config)
        .then(() => {
          setLoading(false);
          setSuccessModal(true);
        })
        .catch((error) => {
          setLoading(false);
          setErrorModal(true);
          setErrorMsg(error?.message);

          let errorObj = {
            errorCode: error?.response?.status,
            errorMessage: error?.message,
          };
          setErrorList((prevList) => [...prevList, errorObj]);
          setOpenToastError(true);
        });
    }
  };

  const handleToastBarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenToastError(false);
  };

  return (
    <div className="create-user-dashboard">
      <Paper className="paper">
        <Grid container direction="row" p={1} className="grid-Heading">
          <Grid
            item
            xs={12}
            sx={{ paddingBottom: 1, display: "flex", flexDirection: "row" }}
          >
            <Button
              onClick={() => {
                navigate(-1);
              }}
              sx={{
                color: "#F0F0F0",
                padding: "0vh 0vh 0.7vh 0vh",
                fontSize: "1.1 rem",
                justifyContent: "start",
                minWidth: "1vw",
                minHeight: "0.5vh",
              }}
            >
              <ArrowBackIosIcon
                data-testid="arrow-back-icon"
                sx={{ fontSize: "0.9 rem" }}
              />
            </Button>
            <Typography className="mainheading">Edit Honeywell User</Typography>
          </Grid>

          <Grid
            container
            direction="column"
            sx={{
              height: "30vh",
              backgroundColor: "#272727",
              minWidth: "800px",
              minHeight: "200px",
              width: "99%",
              border: "1px solid #101010",
              boxShadow:
                "1px 1px 1px rgba(0, 0, 0, 0.15), 1px 1px 1px rgba(0, 0, 0, 0.15)",
            }}
          >
            <Grid
              item
              sx={{
                height: "20%",
                background: "#272727",
              }}
            >
              <Typography className="secondaryHeading">
                PROFILE INFORMATION
              </Typography>
            </Grid>
            <Grid
              item
              sx={{
                height: "80%",
              }}
            >
              <Grid
                container
                direction="row"
                sx={{
                  width: "100%",
                  height: "100%",
                }}
              >
                <Grid
                  item
                  sx={{
                    width: "18%",
                    minHeight: "120px",
                    minWidth: "120px",
                  }}
                >
                  {existingProfilePic === editLogo ? (
                    <UploadProfilePic
                      profilePic={existingProfilePic}
                      onImageUpload={(image) => {
                        setUserProfilePic(image);

                        isUserUpdated.current = {
                          ...isUserUpdated.current,
                          profilePicUpdate: true,
                        };

                        handleEnableOrDisableUpdateButton();
                      }}
                      isUploadDisabled={false}
                    />
                  ) : (
                    <UploadProfilePic
                      profilePic={`data:image/jpeg;base64,${existingProfilePic}`}
                      onImageUpload={(image) => {
                        setUserProfilePic(image);
                        isUserUpdated.current = {
                          ...isUserUpdated.current,
                          profilePicUpdate: true,
                        };

                        handleEnableOrDisableUpdateButton();
                      }}
                      isUploadDisabled={false}
                    />
                  )}
                </Grid>
                <Grid
                  item
                  sx={{
                    width: "80%",
                    display: "flex",
                  }}
                >
                  <Grid
                    sx={{
                      width: "50%",
                      padding: "1%",
                    }}
                  >
                    <TextInputField
                      fieldLabel="First Name"
                      disabled
                      id="FirstName"
                      type="text"
                      value={firstName}
                      required={true}
                      dataTestId="firstName"
                      styleName="edit-text-input-field"
                    />
                    <TextInputField
                      disabled
                      fieldLabel="Email ID"
                      id="EmailID"
                      type="text"
                      value={emailId}
                      required={true}
                      dataTestId="emailId"
                      styleName="edit-text-input-field"
                    />
                  </Grid>
                  {/* Last Name Assign Roles input fields ******************************** */}
                  <Grid
                    sx={{
                      width: "50%",
                      padding: "1%",
                    }}
                  >
                    {" "}
                    <TextInputField
                      disabled
                      fieldLabel="Last Name"
                      id="LastName"
                      type="text"
                      value={lastName}
                      required={true}
                      dataTestId="lastName"
                      styleName="edit-text-input-field"
                    />
                    <AutoCompleteMultiSelect
                      label="Assign Roles"
                      required
                      options={roleOptions}
                      onChange={assignRolesHandler}
                      hasError={isRoleIdEmpty}
                      helperText={isRoleIdEmpty ? "Please select roles" : ""}
                      defaultValues={defaultRoles}
                      readOnlyDefaultValueOptions={readOnlyDefaultRoleOptions}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {/* Accept terms and conditions checkbox with text********************** */}
          <AcceptTerms
            isFormFilled={isFormFilled}
            isTermsAccepted={isTermsAccepted}
            onTermsAccept={(event) => {
              setIsTermsAccepted(event?.target.checked);
            }}
          />
        </Grid>
      </Paper>

      {/* cancel and create buttons************************************************** */}
      <Grid
        item
        xs={12}
        sx={{
          backgroundColor: "#272727",
          width: "98%",
          border: "1px solid #101010",
          boxShadow:
            "1px 1px 1px rgba(0, 0, 0, 0.15), 1px 1px 1px rgba(0, 0, 0, 0.15)",
          display: "flex",
          position: "fixed",
          bottom: 1,
          height: "8vh",
        }}
      >
        <Grid sx={{ height: "3vh", margin: "0.5vh" }}>
          <Button
            color="primary"
            fullWidth
            sx={{
              height: "1vh",
              margin: "1vw",
              position: "relative",
            }}
            className="cancelButton"
            onClick={() => cancelBtnHandler()}
          >
            Cancel
          </Button>
        </Grid>
        <Grid
          container
          justifyContent="flex-end"
          sx={{ height: "3vh", paddingRight: "1.5vw", margin: "0.5vh" }}
        >
          <Button
            sx={{
              fontSize: 12,
              padding: 2,
              height: "1vh",
              position: "relative",
              margin: "0.5vw",
              opacity: !isFormDataChanged ? "0.4" : "1",
            }}
            className="createButton"
            variant="contained"
            onClick={updateBtnHandler}
            disabled={!isFormDataChanged}
          >
            Update
          </Button>
        </Grid>
      </Grid>
      {(loading || isFetchRolesLoading) && <Loader />}

      <InfoModal
        dialogTitle="Successfully Updated"
        dialogContentText="User Account has been updated."
        onOkClick={() => {
          setSuccessModal(false);
          navigate(-1);
        }}
        open={successModal}
      />
      {errorList?.length > 0 && (
        <ToastBarForError
          open={openToastError}
          onClose={handleToastBarClose}
          errorList={errorList}
        />
      )}
    </div>
  );
}

export default EditHoneywellUser;
